import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Car, Users, Baby } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OperatorResult = ({ op }) => (
  <div className="p-4 border rounded-xl bg-white space-y-3" data-testid={`search-result-${op.user_id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <p className="font-semibold">{op.full_name}</p>
      <Badge variant={op.status === 'active' ? 'secondary' : 'destructive'}>{op.status}</Badge>
    </div>
    <div className="text-sm text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
      <span>{op.email}</span>
      <span>{op.phone || 'No phone'}</span>
      <span>Company reg: {op.company_registration_number || 'N/A'}</span>
      <span>SARS: {op.sars_tax_number || 'N/A'}</span>
    </div>
    <div className="flex gap-4 text-xs text-muted-foreground">
      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {op.driver_count} driver(s)</span>
      <span className="flex items-center gap-1"><Baby className="w-3.5 h-3.5" /> {op.kid_count} learner(s)</span>
    </div>
    {op.vehicles.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {op.vehicles.map((v) => (
          <span key={v.vehicle_id} className={`text-xs px-2 py-1 rounded-lg border flex items-center gap-1 ${v.matched ? 'bg-yellow-100 border-yellow-400 font-semibold' : 'bg-slate-50'}`} data-testid={`search-vehicle-${v.registration_number}`}>
            <Car className="w-3.5 h-3.5" /> {v.registration_number} · {v.make} {v.model} · {v.status}{v.driver_name ? ` · ${v.driver_name}` : ''}
          </span>
        ))}
      </div>
    )}
  </div>
);

export const TransportSearch = ({ open, onOpenChange, token }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) {
      toast.error('Type at least 2 characters.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/search/transport`, { params: { q: q.trim() }, headers: { Authorization: `Bearer ${token}` } });
      setResults(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transport-search-dialog">
        <DialogHeader>
          <DialogTitle>Find a transport operator</DialogTitle>
          <DialogDescription>Search by operator name, email, phone, company number or number plate.</DialogDescription>
        </DialogHeader>
        <form onSubmit={run} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="e.g. Vusi or MNM 954 GP" value={q} onChange={(e) => setQ(e.target.value)} data-testid="transport-search-input" autoFocus />
          </div>
          <Button type="submit" disabled={busy} className="rounded-full" data-testid="transport-search-submit">{busy ? 'Searching...' : 'Search'}</Button>
        </form>
        <div className="space-y-3" data-testid="transport-search-results">
          {results && results.length === 0 && <p className="text-sm text-muted-foreground" data-testid="transport-search-empty">No operator or vehicle matches “{q}”.</p>}
          {results && results.map((op) => <OperatorResult key={op.user_id} op={op} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
